import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { MF_SUPPORT_URL } from '../../utils/legalLinks';
import BottomSheet from './BottomSheet';

const GUIDE_ROWS = [
  {
    check: 'Pipeline',
    look: 'Floor plan submissions and processing time',
    act: 'Includes uploads MagicFurnish helps with. If failures spike, open Submissions or Recent Activity.',
  },
  {
    check: 'Audience',
    look: 'Visitors, listing interest, shuffles, and time in the editor',
    act: 'How people interact with your staged links. Use Filters to include or leave out your team or agents.',
  },
  {
    check: 'Results',
    look: 'Sign-ups, shares, and downloads',
    act: 'Tap Sign-Ups to see who joined and what they did next.',
  },
  {
    check: 'Recent Activity',
    look: 'Latest uploads and gallery events (expand the section)',
    act: 'Always shows everyone — not affected by Filters.',
  },
  {
    check: 'Listing performance',
    look: 'Summary band plus Linked / Not Linked tabs',
    act: 'Linked listings show all activity on the floor plan and copies. Link the original agent upload — client copies share the parent listing.',
  },
  {
    check: 'Filters',
    look: 'Icon in the page header',
    act: 'Change visitor and sign-up counts only. Uploads and Recent Activity always stay the same.',
  },
];

const GUIDE_LINK_BUTTON_SX = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.4)',
  fontSize: '0.78rem',
  fontFamily: 'inherit',
  p: 0,
  textTransform: 'none !important',
  '&:hover': {
    color: 'rgba(0,0,0,0.7)',
    textDecoration: 'underline',
  },
};

/**
 * Page-bottom "About this dashboard" affordance (master admin only).
 *
 * Renders as a single quiet link in the page footer; tapping it opens a
 * shared BottomSheet whose body is the existing GUIDE_ROWS playbook. The
 * inline collapsible card the page used to show is gone, freeing top-of-page
 * real estate for data instead of documentation.
 */
const PartnerAnalyticsGuide = ({ onLinkListing = null }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box sx={{ textAlign: 'center', pt: { xs: 2, sm: 6 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ flexWrap: 'wrap' }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            sx={GUIDE_LINK_BUTTON_SX}
          >
            About this dashboard
          </Box>
          {onLinkListing && (
            <>
              <Typography
                component="span"
                sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.28)', lineHeight: 1, userSelect: 'none' }}
              >
                ·
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={onLinkListing}
                sx={GUIDE_LINK_BUTTON_SX}
              >
                Link a Listing
              </Box>
            </>
          )}
        </Stack>
      </Box>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title="About this dashboard"
      >
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: 'rgba(0,0,0,0.55)',
            lineHeight: 1.55,
            mb: 1.5,
          }}
        >
          See whether uploads are working, whether people are viewing your staged
          links, and who signed up. Upload counts load first; visitor details
          fill in when the rest of the page finishes loading.
        </Typography>
        <Stack spacing={1.25}>
          {GUIDE_ROWS.map((row) => (
            <Box
              key={row.check}
              sx={{
                py: 1,
                borderTop: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'rgba(0,0,0,0.65)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  mb: 0.35,
                }}
              >
                {row.check}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)', lineHeight: 1.45 }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.62)' }}>
                  Look at:{' '}
                </Box>
                {row.look}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.45, mt: 0.25 }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.58)' }}>
                  If needed:{' '}
                </Box>
                {row.act}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.42)', mt: 1.5, lineHeight: 1.5 }}>
          Questions?{' '}
          <Box
            component="a"
            href={MF_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'rgba(0,0,0,0.55)', textDecoration: 'underline' }}
          >
            Contact MagicFurnish Support
          </Box>
          .
        </Typography>
      </BottomSheet>
    </>
  );
};

export default PartnerAnalyticsGuide;
